import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigation } from '../contexts/NavigationContext';
import { IconWrench } from '../components/Icons';

const MaintenanceView = () => {
  const { t } = useLanguage();
  const { navigateTo } = useNavigation();
  
  const [requests] = useState([
    {
      id: 1,
      title: 'Bathroom faucet leak',
      property: 'Makati Condo Unit 301',
      requester: 'Juan Dela Cruz',
      status: 'new',
      priority: 'high',
      requestDate: '2024-01-20',
    },
    {
      id: 2,
      title: 'Aircon not working',
      property: 'BGC Apartment 201',
      requester: 'Maria Santos',
      status: 'inProgress',
      priority: 'medium',
      requestDate: '2024-01-18',
      assignedTo: 'Pedro Technician',
    },
    {
      id: 3,
      title: 'Door lock repair',
      property: 'Quezon City Townhouse 102',
      requester: 'Jose Rizal',
      status: 'completed',
      priority: 'low',
      requestDate: '2024-01-15',
      completedDate: '2024-01-17',
      assignedTo: 'Carlos Repair',
    },
  ]);

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      high: { bg: 'bg-red-100', text: 'text-red-800', label: t('high') },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('medium') },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: t('low') },
    };

    const config = priorityConfig[priority];
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { bg: 'bg-blue-100', text: 'text-blue-800', label: t('new') },
      inProgress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('inProgress') },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: t('completed') },
    };

    const config = statusConfig[status];
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const newRequests = requests.filter(r => r.status === 'new');
  const ongoingRequests = requests.filter(r => r.status === 'inProgress' || r.status === 'completed');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('maintenance')}</h1>

      {/* New Maintenance Requests */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <IconWrench className="w-6 h-6 mr-2" />
          {t('newRequests')}
        </h2>
        {newRequests.length === 0 ? (
          <p className="text-gray-500">{t('noRequests')}</p>
        ) : (
          <div className="space-y-4">
            {newRequests.map((request) => (
              <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{request.title}</h3>
                    <p className="text-sm text-gray-600">{request.property}</p>
                    <p className="text-xs text-gray-500">Requested by: {request.requester} | {request.requestDate}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(request.status)}
                    {getPriorityBadge(request.priority)}
                  </div>
                </div>
                <div className="flex space-x-2 mt-3">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md text-sm">
                    {t('detailsAndAssign')}
                  </button>
                  <button 
                    onClick={() => navigateTo('technicianDirectory')}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md text-sm"
                  >
                    {t('findTechnician')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ongoing/Completed Work */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">{t('ongoingCompletedWork')}</h2>
        {ongoingRequests.length === 0 ? (
          <p className="text-gray-500">{t('noRequests')}</p>
        ) : (
          <div className="space-y-4">
            {ongoingRequests.map((request) => (
              <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{request.title}</h3>
                    <p className="text-sm text-gray-600">{request.property}</p>
                    <p className="text-xs text-gray-500">
                      Requested by: {request.requester} | Assigned to: {request.assignedTo}
                    </p>
                    {request.completedDate && (
                      <p className="text-xs text-gray-500">Completed: {request.completedDate}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(request.status)}
                    {getPriorityBadge(request.priority)}
                  </div>
                </div>
                {request.status === 'completed' && (
                  <div className="mt-3">
                    <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md text-sm">
                      {t('evaluateService')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceView;
